import type { ChangeEvent, DragEvent, InputHTMLAttributes } from "react";

import { useRef, useState } from "react";
import { useController } from "react-hook-form";
import { Upload, Loader2 } from "lucide-react";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";

export interface FileUploadStepBaseProps {
	name: string;
	accept: InputHTMLAttributes<HTMLInputElement>["accept"];
	label?: string;
	multiple?: InputHTMLAttributes<HTMLInputElement>["multiple"];
	helperText?: string;
	btnText?: string;
	btnColor?: string;
	btnVariant?: string;
}

export interface VideoUploadProps extends FileUploadStepBaseProps {
	fileType: "video";
	imageProfile?: undefined;
}

export interface ImageUploadProps extends FileUploadStepBaseProps {
	fileType: "image";
	imageProfile: ImageProfile;
}

export type FileUploadStepProps = VideoUploadProps | ImageUploadProps;

const FileUploadStep = ({
	name,
	label,
	accept,
	multiple = false,
	imageProfile,
	fileType
}: FileUploadStepProps) => {
	const { field } = useController({ name });
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const {
		upload: uploadFileToAzure,
		uploading: isUploading,
		progress
	} = useAzureFileUpload();

	const videoUuid = fileType === "video" && field.value ? field.value : null;
	const {
		url: videoUrl,
		thumbnail: videoThumbnail,
		processingStatus
	} = useAzureVideoDownload("video", "", videoUuid);

	const handleClick = () => {
		inputRef.current?.click();
	};

	const validateFile = (file: File) => {
		if (!accept) return true;

		const allowedExtensions = accept
			.split(",")
			.map((type) => type.trim().toUpperCase().replace("IMAGE/", ""));

		const isValid = allowedExtensions.includes(
			file.type.toUpperCase().replace("IMAGE/", "")
		);

		if (!isValid) {
			alert(`Only ${allowedExtensions.join(" / ")} file types can be selected`);
		}

		return isValid;
	};

	const processUpload = async (file: File) => {
		if (!validateFile(file)) return;

		try {
			if (fileType === "image") {
				const uuidList = await uploadFileToAzure(
					file,
					"images",
					undefined,
					imageProfile
				);
				field.onChange(uuidList[0]);
			}

			if (fileType === "video") {
				const videoUuid = await uploadFileToAzure(file, "video");
				field.onChange(videoUuid);
			}
		} catch (error) {
			console.error("Failed to upload file to azure services", error);
		}
	};

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const fileList = event.target.files;
		if (!fileList) return;
		await processUpload(fileList[0]);
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = async (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (!file) return;
		await processUpload(file);
	};

	const handleRemoveFile = () => {
		field.onChange("");
		if (inputRef.current) inputRef.current.value = "";
	};

	const hasFile = !!field.value;

	return (
		<div className="max-w-[480px]">
			{label ? (
				<label className="block text-sm font-medium mb-1">{label}</label>
			) : (
				<span className="sr-only">Upload a file</span>
			)}

			<input
				ref={inputRef}
				type="file"
				value=""
				onChange={handleChange}
				accept={accept}
				multiple={multiple}
				style={{ display: "none" }}
			/>

			{hasFile && !isUploading ? (
				<>
					{fileType === "image" && (
						<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
							<AzureImage
								uuid={field.value}
								imageProfile={imageProfile}
								style={{
									objectFit: "contain",
									width: "100%",
									height: "auto"
								}}
								alt="Uploaded image preview"
							/>
						</div>
					)}
					{fileType === "video" && (
						<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
							{videoUrl ? (
								<video
									src={videoUrl}
									poster={videoThumbnail || undefined}
									controls
									className="w-full object-contain"
									style={{ aspectRatio: "16/9" }}
									preload="metadata"
								/>
							) : processingStatus === "Processing" ? (
								<div
									className="flex flex-col items-center justify-center gap-2 bg-foreground"
									style={{ aspectRatio: "16/9" }}
								>
									<Loader2 className="h-8 w-8 text-white animate-spin" />
									<span className="text-sm text-white/70">
										Processing video...
									</span>
								</div>
							) : videoThumbnail ? (
								<div
									className="w-full bg-no-repeat bg-cover bg-center"
									style={{
										backgroundImage: `url(${videoThumbnail})`,
										aspectRatio: "16/9"
									}}
								/>
							) : (
								<div
									className="flex flex-col items-center justify-center gap-2 bg-foreground"
									style={{ aspectRatio: "16/9" }}
								>
									<Loader2 className="h-8 w-8 text-white animate-spin" />
									<span className="text-sm text-white/70">
										Processing video...
									</span>
								</div>
							)}
						</div>
					)}
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={handleClick}
						>
							{fileType === "image" ? "Change image" : "Change video"}
						</button>
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
							onClick={handleRemoveFile}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => !isUploading && handleClick()}
					onKeyDown={(e) => e.key === "Enter" && !isUploading && handleClick()}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary-lighter"
							: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50",
						isUploading ? "pointer-events-none opacity-60" : ""
					].join(" ")}
				>
					{isUploading ? (
						<>
							<div className="w-full max-w-[200px] h-2 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full bg-primary rounded-full transition-all duration-300"
									style={{ width: `${progress}%` }}
								/>
							</div>
							<span className="text-sm text-muted-foreground">
								Uploading...
							</span>
						</>
					) : (
						<>
							<Upload className="h-6 w-6 text-muted-foreground" />
							<div>
								<p className="text-sm font-medium text-foreground">
									Drag {fileType} here or click to browse
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{fileType === "image" ? "JPG or PNG" : "MP4"}
								</p>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default FileUploadStep;
